import { RatDisplayInfo, SimData } from '../constants/sim';

type NullableString = string | null | undefined;

export type NetworkDetailsBlock = {
  networkName?: string;
  networkCode?: string;
  countryName?: string;
  isoCode?: string;
  region?: string;
  mnc?: string;
  mcc?: string;
  plmn?: string;
  lastRatFriendlyName?: string;
  lastRatCode?: string;
  lastRatTooltip?: string;
};

type ExtendedNetworkDetailsSource = SimData & {
  homeNetworkOperatorName?: string;
  homeNetworkTadig?: string;
  homeNetworkCountryName?: string;
  homeNetworkIsoCode?: string;
  homeNetworkRegion?: string;
  homeNetworkMnc?: string;
  homeNetworkMcc?: string;
  homeNetworkPlmn?: string;
  lastRoamingTadig?: string;
  lastRoamingOperatorName?: string;
  lastRoamingCountryName?: string;
  lastRoamingIsoCode?: string;
  lastRoamingMnc?: string;
  lastRoamingMcc?: string;
  lastRoamingPlmn?: string;
  lastRoamingRatType?: string;
  lastRoamingRatDisplayInfo?: RatDisplayInfo;
};

const toOptionalString = (value: NullableString) => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const toOptionalNumberString = (value?: number | null) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  return `${value}`;
};

const hasValue = (value?: string) => value !== undefined;

const hasAnyValue = (block?: NetworkDetailsBlock) => {
  if (!block) {
    return false;
  }

  return [
    block.networkName,
    block.networkCode,
    block.countryName,
    block.isoCode,
    block.region,
    block.mnc,
    block.mcc,
    block.plmn,
    block.lastRatFriendlyName,
    block.lastRatCode,
    block.lastRatTooltip,
  ].some(hasValue);
};

const getRatCode = (ratDisplayInfo?: RatDisplayInfo | null) => {
  if (!ratDisplayInfo) {
    return undefined;
  }

  const ratCodeParts = [
    toOptionalString(ratDisplayInfo.shortCode),
    toOptionalNumberString(ratDisplayInfo.code),
  ].filter((part): part is string => part !== undefined);

  return ratCodeParts.length > 0 ? ratCodeParts.join(' · ') : undefined;
};

const getHomeNetworkCode = (networkData: ExtendedNetworkDetailsSource) =>
  toOptionalString(networkData.homeNetworkTadig) ??
  toOptionalString(networkData.networkCodeTranslation?.mccMnc?.tadig) ??
  toOptionalString(networkData.networkCodeTranslation?.plmn?.tadig) ??
  toOptionalString(networkData.tadig);

/**
 * Builds the network detail blocks for SIM and rSIM detail pages.
 *
 * Home-network values are preserved from the profile payload, while last-roaming
 * values prefer dedicated session-enriched fields when they are available.
 */
export const getNetworkDetailsData = (data: SimData) => {
  const networkData = data as ExtendedNetworkDetailsSource;

  const homeNetwork: NetworkDetailsBlock = {
    networkName:
      toOptionalString(networkData.mobileNetworkOperator) ??
      toOptionalString(networkData.homeNetworkOperatorName),
    networkCode: getHomeNetworkCode(networkData),
    countryName: toOptionalString(networkData.homeNetworkCountryName),
    isoCode: toOptionalString(networkData.homeNetworkIsoCode),
    region: toOptionalString(networkData.homeNetworkRegion),
    mnc: toOptionalString(networkData.homeNetworkMnc),
    mcc: toOptionalString(networkData.homeNetworkMcc),
    plmn: toOptionalString(networkData.homeNetworkPlmn),
  };

  const lastRoamingRatDisplayInfo = networkData.lastRoamingRatDisplayInfo;
  const lastRoamingNetwork: NetworkDetailsBlock = {
    networkName: toOptionalString(networkData.lastRoamingOperatorName),
    networkCode: toOptionalString(networkData.lastRoamingTadig),
    countryName: toOptionalString(networkData.lastRoamingCountryName),
    isoCode: toOptionalString(networkData.lastRoamingIsoCode),
    mnc: toOptionalString(networkData.lastRoamingMnc),
    mcc: toOptionalString(networkData.lastRoamingMcc),
    plmn: toOptionalString(networkData.lastRoamingPlmn),
    lastRatFriendlyName: toOptionalString(
      lastRoamingRatDisplayInfo?.friendlyName ?? networkData.lastRoamingRatType,
    ),
    lastRatCode: getRatCode(lastRoamingRatDisplayInfo),
    lastRatTooltip: toOptionalString(lastRoamingRatDisplayInfo?.tooltip),
  };

  return {
    hasNetworkIdentifiers: hasAnyValue(homeNetwork) || hasAnyValue(lastRoamingNetwork),
    homeNetwork: hasAnyValue(homeNetwork) ? homeNetwork : undefined,
    lastRoamingNetwork: hasAnyValue(lastRoamingNetwork) ? lastRoamingNetwork : undefined,
  };
};


